import { useEffect, useState } from 'react';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import { validateMembership } from '../adapters/auth/authApi.ts';
import { useI18n } from '../i18n/index.ts';

export const PATREON_HANDOFF_KEY = 'rikosub:patreon-handoff';
export const PATREON_MESSAGE_TYPE = 'rikosub:patreon-login';

export type LoginCallbackPageProps = {
  settingsStore: SettingsStore;
  onDone: () => void;
};

function readCredsFromLocation(): { userId: string; userToken: string } {
  if (typeof window === 'undefined') return { userId: '', userToken: '' };
  const params = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const userId =
    params.get('uid')
    ?? params.get('userId')
    ?? hash.get('uid')
    ?? hash.get('userId')
    ?? document.getElementById('userId')?.textContent?.trim()
    ?? '';
  const userToken =
    params.get('token')
    ?? params.get('userToken')
    ?? hash.get('token')
    ?? hash.get('userToken')
    ?? document.getElementById('userToken')?.textContent?.trim()
    ?? '';
  return { userId: userId.trim(), userToken: userToken.trim() };
}

/**
 * Receives Patreon OAuth credentials (query/hash/DOM) and writes them into settings.
 * Also notifies an opener tab via postMessage + localStorage handoff (extension pickup parity).
 */
export function LoginCallbackPage({ settingsStore, onDone }: LoginCallbackPageProps) {
  const { t } = useI18n();
  const [message, setMessage] = useState(t('loginCallbackWorking'));

  useEffect(() => {
    let cancelled = false;
    let tries = 0;

    const finish = async (userId: string, userToken: string) => {
      await settingsStore.set({ userId, userToken });
      try {
        const validated = await validateMembership(userId, userToken);
        await settingsStore.set({ statusJson: validated.statusJson });
      } catch {
        // keep credentials even if validate fails
      }
      try {
        localStorage.setItem(
          PATREON_HANDOFF_KEY,
          JSON.stringify({ userId, userToken, at: Date.now() }),
        );
      } catch {
        // ignore
      }
      if (window.opener && !window.opener.closed) {
        try {
          window.opener.postMessage(
            { type: PATREON_MESSAGE_TYPE, userId, userToken },
            window.location.origin,
          );
        } catch {
          // ignore
        }
      }
      setMessage(t('loginCallbackOk'));
      window.setTimeout(() => {
        if (!cancelled) onDone();
      }, 400);
    };

    const attempt = () => {
      const creds = readCredsFromLocation();
      if (creds.userId && creds.userToken) {
        void finish(creds.userId, creds.userToken);
        return true;
      }
      return false;
    };

    if (attempt()) return;

    const timer = window.setInterval(() => {
      tries += 1;
      if (attempt() || tries >= 25) {
        window.clearInterval(timer);
        if (tries >= 25 && !cancelled) {
          setMessage(t('loginCallbackFail'));
        }
      }
    }, 100);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [settingsStore, onDone, t]);

  return (
    <main className="login-callback" data-testid="login-callback">
      <h1>{t('appName')}</h1>
      <p data-testid="login-callback-msg">{message}</p>
      <button type="button" className="btn" onClick={onDone}>
        {t('loginCallbackContinue')}
      </button>
    </main>
  );
}
